import React from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  IconButton,
  Toolbar,
} from '@mui/material';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import MenuRounded from '@mui/icons-material/MenuRounded';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import lanFooter from '../assets/lan-footer.png';
import { KNOW_YOUR_CHICKEN_PATH } from '../config/footerLinks';
import { paths } from '../config/paths';
import { v2Colors } from '../design/theme';
import { useCartItemCount } from '../hooks/useCartItemCount';
import { useV2Common } from '../state/CommonProvider';

const NAV = [
  { label: 'Home', to: paths.home, test: (p) => p === paths.home },
  { label: 'Categories', to: paths.categories, test: (p) => p.startsWith(paths.categories) },
  { label: 'Cart', to: paths.cart, test: (p) => p.startsWith(paths.cart), cart: true },
  {
    label: 'Know your chicken',
    to: KNOW_YOUR_CHICKEN_PATH,
    test: (p) => p.startsWith(KNOW_YOUR_CHICKEN_PATH),
  },
];

function NavButton({ label, to, test, cart, pathname, cartIcon }) {
  const active = test(pathname);
  return (
    <Button
      component={RouterLink}
      to={to}
      color="inherit"
      startIcon={cart ? cartIcon : undefined}
      sx={{
        textTransform: 'none',
        fontWeight: active ? 700 : 500,
        fontSize: { md: '0.875rem', lg: '0.95rem' },
        px: { md: 1, lg: 1.5 },
        py: 0.75,
        borderRadius: 2,
        color: active ? 'primary.main' : 'text.primary',
        bgcolor: active ? `${v2Colors.primary}14` : 'transparent',
        '&:hover': { bgcolor: active ? `${v2Colors.primary}1F` : 'action.hover' },
      }}
    >
      {label}
    </Button>
  );
}

export default function TopBar({ onOpenMenu }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDesktop } = useV2Common();
  const cartItemCount = useCartItemCount();

  const cartIcon = (
    <Badge
      color="error"
      max={99}
      badgeContent={cartItemCount}
      invisible={cartItemCount <= 0}
      sx={{
        '& .MuiBadge-badge': {
          fontSize: 10,
          height: 18,
          minWidth: 18,
        },
      }}
    >
      <ShoppingBagOutlined sx={{ fontSize: 22 }} />
    </Badge>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          bgcolor: v2Colors.canvas,
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: { xs: 52, sm: 56, md: 60 },
            px: { xs: 1, md: 2 },
            maxWidth: { md: 1280 },
            width: '100%',
            minWidth: 0,
            mx: { md: 'auto' },
            gap: { xs: 1, md: 1.5, lg: 2 },
            boxSizing: 'border-box',
          }}
        >
          {isDesktop ? (
            <>
              <Box
                component="button"
                type="button"
                onClick={() => navigate(paths.home)}
                sx={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  p: 0,
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
                aria-label="Home"
              >
                <Box
                  component="img"
                  src={lanFooter}
                  alt="Naatu"
                  sx={{
                    height: 40,
                    width: 'auto',
                    maxWidth: 200,
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  minWidth: 0,
                }}
              >
                {NAV.map(({ label, to, test, cart }) => (
                  <NavButton
                    key={to}
                    label={label}
                    to={to}
                    test={test}
                    cart={cart}
                    pathname={pathname}
                    cartIcon={cartIcon}
                  />
                ))}
              </Box>
              <IconButton
                edge="end"
                aria-label="Account shortcut"
                size="large"
                onClick={() => navigate(paths.account)}
                sx={{ flexShrink: 0, color: 'text.primary' }}
              >
                <AccountCircleOutlined sx={{ fontSize: 30 }} />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                edge="start"
                aria-label="Open menu"
                size="large"
                onClick={() => onOpenMenu?.()}
                sx={{
                  flexShrink: 0,
                  color: 'text.primary',
                  '& .MuiSvgIcon-root': { fontSize: 32 },
                }}
              >
                <MenuRounded />
              </IconButton>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: 0,
                  px: 1,
                }}
              >
                <Box
                  component="img"
                  src={lanFooter}
                  alt="Naatu"
                  sx={{
                    height: { xs: 36, sm: 44 },
                    maxWidth: 'min(360px, 82vw)',
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <IconButton
                edge="end"
                aria-label="Account"
                size="large"
                onClick={() => navigate(paths.account)}
                sx={{
                  flexShrink: 0,
                  color: 'text.primary',
                  mr: 1,
                  '& .MuiSvgIcon-root': { fontSize: 32 },
                }}
              >
                <AccountCircleOutlined />
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar
        variant="dense"
        sx={{
          visibility: 'hidden',
          pointerEvents: 'none',
          minHeight: { xs: 52, sm: 56, md: 60 },
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
        aria-hidden
      />
    </>
  );
}
